import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  orderBy,
  limit,
} from "firebase/firestore";
import { getFirebaseDB } from "@/lib/firebase";
import { LeaseAgreement, MaintenanceRequest, Property } from "@/types/types";
import { getMaintenanceRequests } from "@/lib/maintenance-service";

/**
 * Get tenant's active lease agreement
 * Returns the most recent active lease for the tenant
 */
export async function getTenantActiveLease(
  tenantId: string
): Promise<LeaseAgreement | null> {
  try {
    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase not initialized");

    const leasesRef = collection(db, "leases");

    // Try to get leases in priority order: active > pending_signatures > pending_tenant_signup
    const statuses = ["active", "pending_signatures", "pending_tenant_signup"];

    for (const status of statuses) {
      const q = query(
        leasesRef,
        where("tenantId", "==", tenantId),
        where("status", "==", status),
        orderBy("createdAt", "desc"),
        limit(1)
      );

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const leaseDoc = snapshot.docs[0];
        const leaseData = {
          id: leaseDoc.id,
          ...leaseDoc.data(),
        } as LeaseAgreement;

        console.log(`Found ${status} lease for tenant:`, leaseData);
        return leaseData;
      }
    }

    console.log("No lease found for tenant:", tenantId);
    return null;
  } catch (error) {
    console.error("Error fetching tenant active lease:", error);
    throw error;
  }
}

/**
 * Get property details for a tenant's lease
 */
export async function getTenantProperty(
  propertyId: string
): Promise<Property | null> {
  try {
    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase not initialized");

    const propertyRef = doc(db, "properties", propertyId);
    const propertySnap = await getDoc(propertyRef);

    if (!propertySnap.exists()) {
      return null;
    }

    return {
      id: propertySnap.id,
      ...propertySnap.data(),
    } as Property;
  } catch (error) {
    console.error("Error fetching tenant property:", error);
    throw error;
  }
}

/**
 * Get all maintenance requests for a tenant
 */
export async function getTenantMaintenanceRequests(
  tenantId: string
): Promise<MaintenanceRequest[]> {
  try {
    return await getMaintenanceRequests({
      userId: tenantId,
      userRole: "tenant",
    });
  } catch (error) {
    console.error("Error fetching tenant maintenance requests:", error);
    throw error;
  }
}

/**
 * Aggregate all dashboard data for a tenant in one call
 * This reduces the number of Firebase reads and provides all necessary data
 */
export async function getTenantDashboardData(tenantId: string) {
  try {
    // Get active lease first
    const activeLease = await getTenantActiveLease(tenantId);

    if (!activeLease) {
      return {
        lease: null,
        property: null,
        maintenanceRequests: [],
        stats: {
          totalRequests: 0,
          pendingRequests: 0,
          completedRequests: 0,
        },
      };
    }

    // Fetch property and maintenance requests in parallel
    const [property, maintenanceRequests] = await Promise.all([
      getTenantProperty(activeLease.propertyId),
      getTenantMaintenanceRequests(tenantId),
    ]);

    // Calculate stats
    const stats = {
      totalRequests: maintenanceRequests.length,
      pendingRequests: maintenanceRequests.filter(
        (r) => r.status === "pending" || r.status === "scheduled"
      ).length,
      completedRequests: maintenanceRequests.filter(
        (r) => r.status === "completed"
      ).length,
    };

    return {
      lease: activeLease,
      property,
      maintenanceRequests,
      stats,
    };
  } catch (error) {
    console.error("Error fetching tenant dashboard data:", error);
    throw error;
  }
}

/**
 * Get tenant's payment history
 * TODO: Implement when payment system is ready
 */
export async function getTenantPaymentHistory(tenantId: string) {
  // Placeholder for future implementation
  console.log("Payment history not yet implemented for tenant:", tenantId);
  return [];
}

/**
 * Get tenant's documents (lease agreement, receipts, etc.)
 * TODO: Implement when document storage is ready
 */
export async function getTenantDocuments(tenantId: string) {
  // Placeholder for future implementation
  console.log("Documents not yet implemented for tenant:", tenantId);
  return [];
}

/**
 * Calculate days until next rent payment
 */
export function getDaysUntilNextPayment(lease: LeaseAgreement): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset to start of day for accurate calculation

  const leaseStart = new Date(lease.startDate);
  leaseStart.setHours(0, 0, 0, 0);

  const leaseEnd = new Date(lease.endDate);
  leaseEnd.setHours(0, 0, 0, 0);

  // If lease hasn't started yet, return days until lease start
  if (today < leaseStart) {
    const diffTime = leaseStart.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  // If lease has ended, return 0
  if (today > leaseEnd) {
    return 0;
  }

  const paymentDay = lease.paymentDueDay || 1;

  // Get next payment date
  let nextPaymentDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    paymentDay
  );
  nextPaymentDate.setHours(0, 0, 0, 0);

  // If payment day has passed this month, move to next month
  if (nextPaymentDate <= today) {
    nextPaymentDate = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      paymentDay
    );
    nextPaymentDate.setHours(0, 0, 0, 0);
  }

  const diffTime = nextPaymentDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Calculate days until lease end
 */
export function getDaysUntilLeaseEnd(lease: LeaseAgreement): number {
  const today = new Date();
  const leaseEnd = new Date(lease.endDate);
  const diffTime = leaseEnd.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Calculate lease progress percentage (0-100)
 */
export function calculateLeaseProgress(lease: LeaseAgreement): number {
  const startDate = new Date(lease.startDate);
  const endDate = new Date(lease.endDate);
  const today = new Date();

  const totalDuration = endDate.getTime() - startDate.getTime();
  const elapsedDuration = today.getTime() - startDate.getTime();

  return Math.min(
    Math.max(Math.round((elapsedDuration / totalDuration) * 100), 0),
    100
  );
}
